import React, { useEffect, useRef, useState } from 'react';
import { getAuth, PhoneAuthProvider, PhoneMultiFactorGenerator, RecaptchaVerifier } from 'firebase/auth';
import { Button, TextField, Typography, Alert, CircularProgress, Box } from '@mui/material';
import { IceButton } from '../styles/authStyles';

// Firebase masks the enrolled number (e.g. "+*******1234"); show only its last digits.
function phoneEnding(resolver) {
  const digits = String(resolver?.hints?.[0]?.phoneNumber || '').replace(/\D/g, '');
  return digits.slice(-4);
}

function MFASecondFactor({ resolver, onSuccess }) {
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [sendStatus, setSendStatus] = useState('sending');
  const [sendAttempt, setSendAttempt] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const recaptchaVerifierRef = useRef(null);
  const submissionPendingRef = useRef(false);

  useEffect(() => {
    let active = true;
    let recaptchaVerifier;
    setVerificationId(null);
    setError('');
    setSendStatus('sending');

    try {
      if (!resolver?.hints?.length) {
        throw new Error('No second factor hint available.');
      }
      const auth = getAuth();
      recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container-mfa', {
        size: 'invisible',
      });
      recaptchaVerifierRef.current = recaptchaVerifier;
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const phoneInfoOptions = {
        multiFactorHint: resolver.hints[0],
        session: resolver.session,
      };
      Promise.resolve(phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier))
        .then((vid) => {
          if (!active) return;
          if (!vid) {
            setError('Could not prepare the verification code. Please try again.');
            setSendStatus('error');
            return;
          }
          setVerificationId(vid);
          setSendStatus('ready');
        })
        .catch((err) => {
          if (!active) return;
          setError(err?.message || 'Could not send the verification code.');
          setSendStatus('error');
        });
    } catch (err) {
      setError(err?.message || 'Could not send the verification code.');
      setSendStatus('error');
    }

    return () => {
      active = false;
      recaptchaVerifier?.clear();
      if (recaptchaVerifierRef.current === recaptchaVerifier) {
        recaptchaVerifierRef.current = null;
      }
    };
  }, [resolver, sendAttempt]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (sendStatus !== 'ready' || !verificationId || submissionPendingRef.current) return;
    setError('');
    setSuccess('');
    if (!verificationCode.trim()) {
      setError("Please enter the verification code.");
      return;
    }
    submissionPendingRef.current = true;
    setIsLoading(true);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, verificationCode.trim());
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(credential);
      await resolver.resolveSignIn(multiFactorAssertion);
      setSuccess("MFA sign-in successful!");
      // Clear MFA resolver state in parent (indicating MFA sign-in is complete)
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      submissionPendingRef.current = false;
      setIsLoading(false);
    }
  };

  const ending = phoneEnding(resolver);
  const resend = () => {
    setVerificationCode('');
    setSendStatus('sending');
    setSendAttempt((attempt) => attempt + 1);
  };

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      <Box>
        <Typography component="h2" sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25 }}>
          Verify it&apos;s you
        </Typography>
        {ending && (
          <Typography color="text.secondary" sx={{ fontSize: 14, mt: 0.5 }}>
            {`Code sent to •••${ending}`}
          </Typography>
        )}
      </Box>
      {sendStatus === 'sending' ? (
        <Box role="status" sx={{ minHeight: 120, display: 'grid', placeContent: 'center', justifyItems: 'center', gap: 2 }}>
          <CircularProgress size={28} aria-label="Sending verification code" />
          <Typography>Sending verification code…</Typography>
        </Box>
      ) : sendStatus === 'error' ? (
        <>
          <Alert severity="error">{error}</Alert>
          <IceButton variant="contained" onClick={resend}>
            Try again
          </IceButton>
        </>
      ) : (
        <Box component="form" aria-label="Verify code" onSubmit={handleSubmit}>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}
          <TextField
            inputProps={{ 'aria-label': 'Verification Code' }}
            placeholder="6-digit code"
            value={verificationCode}
            onChange={(event) => setVerificationCode(event.target.value)}
            autoComplete="one-time-code"
            inputMode="numeric"
            fullWidth
            margin="dense"
            disabled={isLoading}
          />
          <IceButton
            type="submit"
            variant="contained"
            disabled={isLoading || !verificationCode.trim()}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoading ? 'Verifying...' : 'Verify Code and Sign In'}
          </IceButton>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button variant="text" size="small" onClick={resend} disabled={isLoading}>
              Resend code
            </Button>
          </Box>
        </Box>
      )}
      <Box id="recaptcha-container-mfa" sx={{ position: 'absolute', width: 0, height: 0 }} />
    </Box>
  );
}

export default MFASecondFactor;
